import {useQuery} from 'react-query'

import {API} from '../../networking/http'
import {OfferedAddon, OfferedAddonsEntityType, OfferedAddonsResponse} from './types'

export const makeGetOfferedAddonsEndpoint = (venueId: string) => `venues/${encodeURIComponent(venueId)}/addons/offered`

const EMPTY: OfferedAddonsResponse = {currency: null, addons: []}

// A class is asked for by the CLASS id (not the session's); a service by the service id.
export type OfferedAddonsParams = {
  venueId?: string | null
  entityType: OfferedAddonsEntityType
  entityId?: string | null
  // YYYY-MM-DD: hides add-ons outside their dates.
  date?: string
}

export const fetchOfferedAddons = async ({
  venueId,
  entityType,
  entityId,
  date,
}: OfferedAddonsParams): Promise<OfferedAddonsResponse> => {
  if (!venueId || !entityId) {
    return EMPTY
  }
  try {
    const res = await API.get<unknown, OfferedAddonsResponse>(makeGetOfferedAddonsEndpoint(venueId), {
      params: {entityType, entityId, ...(date ? {date} : {})},
    })
    const addons = Array.isArray(res.data?.addons) ? (res.data.addons as OfferedAddon[]) : []
    return {currency: res.data?.currency ?? null, addons}
  } catch (_error) {
    // No add-ons is the safe answer: the booking carries on exactly as it did before.
    return EMPTY
  }
}

// Public read, no sign-in. Never throws: an empty list and a failed call look the same to the booking.
export const useOfferedAddons = (params: OfferedAddonsParams) =>
  useQuery<OfferedAddonsResponse>(
    ['offeredAddons', params.venueId, params.entityType, params.entityId, params.date],
    () => fetchOfferedAddons(params),
    {
      enabled: Boolean(params.venueId && params.entityId),
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
      retry: false,
    },
  )
